import { ReactNode } from 'react';
import { Menu as MuiMenu, MenuItem as MuiMenuItem, Box, Typography } from '@mui/material';

import { Divider } from '../../common/divider';
import { Icon, IconType } from '../../common/icon';
import { PRIMARY_COLOR_LIGHT } from '../../../styles/themes';
import { convertToKebabCase } from '../../../constants/helpers/text';

type MenuItem = {
  label?: string;
  onClick?: () => void;
  icon?: IconType;
  isDivider?: boolean;
};

interface Props {
  children: ReactNode;
  anchorEl: null | HTMLElement;
  items: MenuItem[];
  handleClose: () => void;
  offset?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  placement?: { horizontal: 'right' | 'left'; vertical: 'top' | 'bottom' };
  disabledOption?: string;
  disabledMenu?: boolean;
}

const WithMenu = ({
  children,
  anchorEl,
  items,
  handleClose,
  offset,
  placement = { horizontal: 'right', vertical: 'bottom' },
  disabledOption,
  disabledMenu = false
}: Props) => (
  <Box>
    {children}
    <MuiMenu
      anchorEl={anchorEl}
      open={!disabledMenu && Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={placement}
      sx={{
        '& .MuiPaper-root': {
          marginTop: offset?.top ? `${offset.top}px` : '0',
          marginRight: offset?.right ? `${offset.right}px` : '0',
          marginBottom: offset?.bottom ? `${offset.bottom}px` : '0',
          marginLeft: offset?.left ? `${offset.left}px` : '0',
          boxShadow:
            '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
          '& ul': {
            padding: 0,
            '& li': {
              background: 'white',
              minHeight: '36px',
              height: '36px',
              '&:hover': {
                background: PRIMARY_COLOR_LIGHT
              }
            }
          }
        }
      }}
    >
      {items.map(({ icon, label, onClick, isDivider }, index) => {
        if (isDivider) {
          return <Divider key={index} />;
        }

        return (
          <MuiMenuItem
            disabled={label === disabledOption}
            key={index}
            onClick={() => {
              if (onClick) onClick();
              handleClose();
            }}
          >
            <Box display="flex" alignItems="center" data-testid={label && `${convertToKebabCase(label)}-tab`}>
              {icon ? (
                <Box display="flex" alignItems="center" mr={1}>
                  <Icon icon={icon} />
                </Box>
              ) : null}
              <Typography>{label}</Typography>
            </Box>
          </MuiMenuItem>
        );
      })}
    </MuiMenu>
  </Box>
);

export default WithMenu;
